import { post } from "@/infra/rest";
import { checkAndRefreshUrl } from "@/modules/agent/utils/refreshUrl";
import { AgentSessionStepType, AgentSessionType } from "@/modules/sessions/types";
import { useTeamState } from "@/modules/team/states";
import { ZButton } from "@/shared/components/button";
import FileUpload from "@/shared/components/custom/file-upload";
import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
} from '@/shared/components/drawer';
import { ZTooltip } from "@/shared/components/ZTooltip/ZTooltip";
import SpinnerImage from "@/shared/images/spinner.gif";
import { addNotification } from "@/shared/states/notification";
import { UploadedFile } from "@/shared/types/file-upload";
import { Checkbox } from "@nextui-org/checkbox";
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader
} from "@nextui-org/react";
import dayjs from "dayjs";
import { Loader2 } from 'lucide-react';
import { useState } from "react";
import { downloadControlsTemplate, importControls } from "../../api/controls";
import { getAgentStateActions, useAgentStepData } from "../../states";
import { getCustomizeControlsActions, useCustomizeControlsStore, type VendorAssessmentControl } from "../../states/cusomize-controls";

// Using the ControlSet interface implicitly from the API response
// Fields needed: id, controls_set_name, number_of_controls, default_control_set

interface ImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (files: UploadedFile[], controlsData?: any[], signedUrl?: string, selectedControlSetNames?: string[]) => void;
  loading?: boolean;
  sessionId: string; // Add sessionId prop
  isAgentTypeMultiFile?: boolean; // true if agent type is MULTI_FILE_VENDOR_ASSESSMENT
  controlSets?: any[]; // Add controlSets prop - using any for now since we don't have the type
  isLoadingControlSets?: boolean; // Add loading state for control sets
  isControlSetEdit?: boolean; // Add prop to explicitly indicate control set edit mode
}

const ImportModal = ({
  isOpen,
  onClose,
  onImport,
  loading,
  sessionId, // Accept sessionId
  isAgentTypeMultiFile = false, // default to false
  controlSets = [], // Default to empty array
  isLoadingControlSets = false, // Default to not loading
  isControlSetEdit = false, // Default to false
}: ImportModalProps) => {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const { agentType } = useCustomizeControlsStore();
  const [activeTab, setActiveTab] = useState<"import" | "template">("import");
  const [selectedControlSets, setSelectedControlSets] = useState<Set<string>>(new Set());

  // Get team data for member name resolution
  const { team } = useTeamState();

  // Helper function to get member name from member ID
  const getMemberName = (memberId: string) => {
    const member = team.find((member) => member.member_id === memberId);
    return member?.name || "Unknown";
  };

  // Detect if we're in control set edit mode vs agent session mode
  const isControlSetEditMode = isControlSetEdit;
  
  // Fetch step data to get control_selection_step_id (only for agent session mode)
  const stepData = useAgentStepData(isControlSetEditMode ? '' : sessionId);

  // Helper function to format date
  const formatDate = (dateString: string) => {
    try {
      // Handle the specific date format from API: "2025-09-04T05:12:36.632476"
      const date = dayjs(dateString);
      if (!date.isValid()) {
        return dateString; // Return original if parsing fails
      }
      return date.format('D MMMM YYYY'); // Format as "22 July 2025"
    } catch {
      return dateString;
    }
  };

  const handleDownloadTemplate = async () => {
    setIsDownloading(true);

    // Map agent type to API parameter
    let templateType = "soc2_type2"; // default
    if (agentType === AgentSessionType.SOC2_GAP_TYPE1 || agentType === AgentSessionType.SOC2_GAP_TYPE2) {
      templateType = "soc2_type2"; // Both type 1 and type 2 use soc2_type2
    } else if (agentType === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT) {
      templateType = "multi_file_vendor_assessment";
    }

    await downloadControlsTemplate(templateType);

    // Reset loading after a short delay (since browser handles download)
    setTimeout(() => setIsDownloading(false), 1200);
  };

  const handleImport = async () => {
    try {
      setIsImporting(true);
      
      // Map agent type to API parameter
      let templateType = "soc2_type2"; // default
      if (agentType === AgentSessionType.SOC2_GAP_TYPE1 || agentType === AgentSessionType.SOC2_GAP_TYPE2) {
        templateType = "soc2_type2"; // Both type 1 and type 2 use soc2_type2
      } else if (agentType === AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT) {
        templateType = "multi_file_vendor_assessment";
      }
      
      // Skip session ID for control set edit mode
      const effectiveSessionId = isControlSetEditMode ? '' : sessionId;
      const result = await importControls(uploadedFiles, templateType, effectiveSessionId);
      
      if (result.success) {
        // For control set edit mode, clear old data FIRST before showing notification
        if (isControlSetEditMode && result.controlsData) {
          // Step 1: Clear existing data state immediately to show loader
          const { setDataState } = getCustomizeControlsActions();
          setDataState([]);
          
          // Step 2: Small delay to ensure UI updates
          await new Promise(resolve => setTimeout(resolve, 100));
        }
        
        addNotification({
          title: "Import Successful",
          message: `${result.importedCount} controls imported to the assessment.`,
          type: "success",
        });
        
        // For control set edit mode, import the new data
        if (isControlSetEditMode && result.controlsData) {
          // Step 3: Call onImport with the imported controls data and signedUrl for control set edit context
          onImport(uploadedFiles, result.controlsData, result.signedUrl);
        } else {
          // Call onImport to trigger data refresh for agent sessions
          onImport(uploadedFiles);
        }
        
        setUploadedFiles([]);
        
        // Add 500ms delay before closing the modal for better UX
        setTimeout(() => {
          onClose();
        }, 500);
      }
    } catch (error) {
      addNotification({
        title: "Import Failed",
        message: "Zania was unable to import controls from the file. Ensure the format of the file is correct and try again.",
        type: "error",
      });
      console.error("Import error:", error);
    } finally {
      setIsImporting(false);
    }
  };

  const handleImportFromControlSets = async () => {
    try {
      setIsImporting(true);

      // Get selected control set IDs
      const selectedControlSetIds = Array.from(selectedControlSets);

      if (selectedControlSetIds.length === 0) {
        addNotification({
          title: "No Selection",
          message: "Please select at least one control set to import.",
          type: "warn",
        });
        setIsImporting(false);
        return;
      }

      // Find selected control sets with their URLs
      const selectedSets = controlSets.filter(set => selectedControlSetIds.includes(set.id));
      const setsWithUrls = selectedSets.filter(set => set.controls_url && set.controls_url.trim() !== '');

      if (setsWithUrls.length === 0) {
        addNotification({
          title: "Import Failed",
          message: "Selected control sets have no controls to import.",
          type: "error",
        });
        setIsImporting(false);
        return;
      }

      // Fetch controls from all selected control sets
      const allImportedControls: VendorAssessmentControl[] = [];
      let successCount = 0;

      for (const controlSet of setsWithUrls) {
        try {
          // Refresh URL if needed and fetch controls
          const validSignedUrl = await checkAndRefreshUrl(controlSet.controls_url);
          const response = await fetch(validSignedUrl);
          
          if (!response.ok) {
            console.warn(`Failed to fetch controls from ${controlSet.controls_set_name}`);
            continue;
          }

          const controlsData = await response.json();
          
          // Transform controls to match VendorAssessmentControl interface
          const transformedControls: VendorAssessmentControl[] = controlsData.map((control: any) => ({
            ...control,
            agentType: AgentSessionType.MULTI_FILE_VENDOR_ASSESSMENT,
            uid: control.uid || control.id || `imported_${Math.random().toString(36).substr(2, 9)}`,
            category: control.category || control.key || '',
            question: control.question || '',
            section: control.section || 'zania_security_assessment' as const,
            edited_state: 'default' as const,
            changesInQuestion: 0,
          }));

          allImportedControls.push(...transformedControls);
          successCount++;
        } catch (error) {
          console.warn(`Error importing from ${controlSet.controls_set_name}:`, error);
        }
      }

      if (allImportedControls.length === 0) {
        addNotification({
          title: "Import Failed",
          message: "No controls could be imported from the selected control sets.",
          type: "error",
        });
        setIsImporting(false);
        return;
      }

      // For control set edit mode, clear old data FIRST before showing notification
      if (isControlSetEditMode) {
        // Step 1: Clear existing data state immediately to show loader
        const { setDataState } = getCustomizeControlsActions();
        setDataState([]);
        
        // Step 2: Small delay to ensure UI updates
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      // Get selected control set names for notification
      const selectedNames = setsWithUrls.map(set => set.controls_set_name).join(", ");

      addNotification({
        title: "Import Successful",
        message: `${allImportedControls.length} controls imported from ${selectedNames}.`,
        type: "success",
      });

      // Clear selection
      setSelectedControlSets(new Set());
      
      // For control set edit mode, import the new data
      if (isControlSetEditMode) {
        // Step 3: Use the first control set's URL as the importedControlsUrl
        const firstControlSetUrl = setsWithUrls[0].controls_url;
        onImport([], allImportedControls, firstControlSetUrl);
      } else {
        // Trigger data refresh for agent session mode
        const selectedNamesArray = setsWithUrls.map(set => set.controls_set_name);
        onImport([], undefined, undefined, selectedNamesArray);
      }
      
      setTimeout(() => {
        onClose();
      }, 500);

    } catch (error) {
      console.error("Import error:", error);
      addNotification({
        title: "Import Failed",
        message: "Failed to import selected controls. Please try again.",
        type: "error",
      });
    } finally {
      setIsImporting(false);
    }
  };

  const handleImportFromSelection = async () => {
    try {
      setIsImporting(true);

      // Find the control selection step
      const controlSelectionStep = stepData?.find(
        (step) => step.type === AgentSessionStepType.CONTROL_SELECTION
      );

      if (!controlSelectionStep) {
        throw new Error("Control selection step not found");
      }

      // Get selected control set IDs
      const selectedControlSetIds = Array.from(selectedControlSets);

      if (selectedControlSetIds.length === 0) {
        addNotification({
          title: "No Selection",
          message: "Please select at least one control set to import.",
          type: "warn",
        });
        setIsImporting(false);
        return;
      }

      // Make API call to import from selected control sets
      const response = await post<
        {
          agent_session_id: string;
          control_selection_step_id: string;
          selected_controls: string[];
        },
        {
          success: boolean;
          control_selection_step: any;
        }
      >({
        url: "/v0/custom-control-set/control-selection",
        data: {
          agent_session_id: sessionId,
          control_selection_step_id: controlSelectionStep.id,
          selected_controls: selectedControlSetIds,
        },
      });

      if (response.success && response.control_selection_step) {
        // Update the step data in the store
        const { updateAgentStepData } = getAgentStateActions();
        const updatedSteps = stepData?.map((step) =>
          step.id === controlSelectionStep.id ? response.control_selection_step : step
        ) || [];
        updateAgentStepData(sessionId, updatedSteps);

        // Get selected control set names for notification
        const transformedControlSets = controlSets.map((set) => ({
          id: set.id,
          name: set.controls_set_name,
          count: 0,
          isDefault: set.default_control_set,
        }));
        const displayControlSets = transformedControlSets;
        const selectedSets = displayControlSets.filter(set => selectedControlSets.has(set.id));
        const selectedNames = selectedSets.map(set => set.name).join(", ");

        addNotification({
          title: "Import Successful",
          message: `Controls from ${selectedNames} imported to the assessment.`,
          type: "success",
        });

        // Clear selection and close modal
        setSelectedControlSets(new Set());
        
        // Trigger data refresh with selected control set names
        const selectedNamesArray = selectedSets.map(set => set.name);
        onImport([], undefined, undefined, selectedNamesArray);
        
        setTimeout(() => {
          onClose();
        }, 500);
      } else {
        throw new Error("Failed to import controls");
      }

    } catch (error) {
      console.error("Import error:", error);
      addNotification({
        title: "Import Failed",
        message: "Failed to import selected controls. Please try again.",
        type: "error",
      });
    } finally {
      setIsImporting(false);
    }
  };

  const handleFileChange = (files: UploadedFile[]) => {
    setUploadedFiles(files);
  };

  const toggleControlSet = (id: string) => {
    const newSelectedSets = new Set(selectedControlSets);
    if (newSelectedSets.has(id)) {
      newSelectedSets.delete(id);
    } else {
      newSelectedSets.add(id);
    }
    setSelectedControlSets(newSelectedSets);
  };

  const hasValidFiles = uploadedFiles.some(file => file.status === "success");
  const isProcessing = isDownloading || isImporting || loading;
  const hasSelectedControlSets = selectedControlSets.size > 0;
  const hasFileUploadError = uploadedFiles.some(file =>
    ["duplicate_files", "too_large", "too_many_files", "failed"].includes(file.status)
  );

  // Render the content for the "Import Controls" tab
  const renderImportContent = () => (
    <>
      <div className="flex flex-col gap-4">
        {/* Step 1: Download Template */}
        <div className="flex flex-col gap-1.5">
          <h3 className="text-[14px] font-medium text-[#11181C]">
            Step 1: Download Template
          </h3>
          <p className="text-[14px] text-[#71717A]">
            Use this Excel file to ensure your control data is formatted correctly.
          </p>
          <ZButton
            variant="bordered"
            className="w-fit mt-1 border-1 text-[14px] font-medium"
            size="sm"
            onClick={handleDownloadTemplate}
            isDisabled={isDownloading || isImporting}
          >
            <span>{isDownloading ? <Loader2 className="animate-spin text-sm" /> : null}</span>
            Download Template
          </ZButton>
        </div>

        {/* Step 2: Fill in Details */}
        <div className="flex flex-col gap-1.5">
          <h3 className="text-[14px] font-medium text-[#11181C]">
            Step 2: Fill in Details
          </h3>
          <p className="text-[14px] text-[#71717A]">
            Add your control data into the downloaded file.
          </p>
        </div>

        {/* Step 3: Upload Completed File */}
        <div className="flex flex-col gap-1.5 mb-4">
          <h3 className="text-[14px] font-medium text-[#11181C]">
            Step 3: Upload Completed File
          </h3>
          <FileUpload
            isMultiple={false}
            maxFiles={1}
            allowedFileTypes={[
              "text/csv",
              "application/vnd.ms-excel",
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            ]}
            setFiles={handleFileChange}
            maxSizeMB={20}
          />
        </div>
      </div>

      {isAgentTypeMultiFile && (
        <div className="flex flex-col items-center mt-auto gap-2">
          <ZButton
            color="primary"
            size="md"
            className="font-medium w-full"
            onClick={handleImport}
            isLoading={isImporting}
            isDisabled={!hasValidFiles || isDownloading || isImporting || hasFileUploadError}
          >
            Import Controls from File
          </ZButton>
          <p className="text-[14px] font-normal text-[#F31260] text-center">
            Note that all existing controls will be overridden.
          </p>
        </div>
      )}
    </>
  );

  // Render the content for the "Template" tab
  const renderTemplateContent = () => {
    // Transform API data to match expected format
    const transformedControlSets = controlSets.filter((set: any) => set.number_of_controls > 0).map((set: any) => ({
      id: set.id,
      name: set.controls_set_name,
      count: set.number_of_controls || 0,
      isDefault: set.default_control_set,
      updatedBy: set.last_updated_by,
      updatedOn: set.last_updated_on,
    }));

    // Use transformed data if available, otherwise fallback to mock data
    const displayControlSets = transformedControlSets;

    return (
      <>
        <div className="flex flex-col gap-4 h-full">
          <h3 className="text-[14px] font-medium text-[#11181C]">Controls</h3>
          {isLoadingControlSets ? (
            <div className="flex items-center justify-center py-8">
              <img
                src={SpinnerImage}
                alt="Loading..."
                className="w-8 h-8"
                loading="lazy"
                decoding="async"
              />
            </div>
          ) : (
            <div className="flex flex-col gap-2 h-[60vh] overflow-y-auto">
              {displayControlSets.map((controlSet) => (
                <ZTooltip isDisabled={false} showArrow
                  key={controlSet.id}
                  content={`Last Updated: ${getMemberName(controlSet.updatedBy)} | ${formatDate(controlSet.updatedOn)}`}
                  placement="bottom"
                >
                  <div
                    className="flex items-center p-4 bg-[#FAFAFA] border border-[#E4E4E7] rounded-lg cursor-pointer hover:bg-[#F4F4F5] transition-colors"
                    onClick={() => toggleControlSet(controlSet.id)}
                  >
                    <div className="flex items-center gap-2 flex-1">
                      <Checkbox
                        color="primary"
                        isSelected={selectedControlSets.has(controlSet.id)}
                        onValueChange={() => toggleControlSet(controlSet.id)}
                        onClick={(e) => e.stopPropagation()} // Prevent double triggering
                      />
                      <div className="flex flex-col flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-[14px] text-[#3F3F46]">
                            {controlSet.name}
                          </span>
                          {controlSet.count > 0 && (
                            <span className="text-[12px] text-[#71717A] ml-auto">
                              {controlSet.count} controls
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </ZTooltip>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-col items-center mt-auto gap-2">
          <ZButton
            color="primary"
            size="md"
            className="font-medium w-full"
            onClick={isControlSetEditMode ? handleImportFromControlSets : handleImportFromSelection}
            isLoading={isImporting}
            isDisabled={!hasSelectedControlSets || isProcessing || isLoadingControlSets}
            style={{ opacity: hasSelectedControlSets ? 1 : 0.5 }}
          >
            Import Controls from Selection
          </ZButton>
          <p className="text-[14px] font-normal text-[#F31260] text-center">
            {isControlSetEditMode 
              ? "Controls will be added to the current control set."
              : "Note that all existing controls will be overridden."
            }
          </p>
        </div>
      </>
    );
  };

  // Render content for both modal and drawer
  const renderContent = () => (
    <>
      {isImporting ? (
        <div className="min-h-[500px] flex flex-col items-center justify-center">
          <div className="flex flex-col items-center justify-center py-40">
            <img
              src={SpinnerImage}
              alt="Loading..."
              className="w-16 h-16"
              loading="lazy"
              decoding="async"
            />
          </div>
          <p className="text-lg text-center mt-4 text-[#171717]">AI is importing controls from your file</p>
        </div>
      ) : (
          <>
            {isAgentTypeMultiFile && (
              <div className="p-1 bg-[#F4F4F5] rounded-lg mb-4">
                <div className="flex">
                  <button
                    className={`flex-1 py-1 px-3 text-center rounded-md ${activeTab === "import" ? "bg-white shadow-sm" : ""}`}
                    onClick={() => setActiveTab("import")}
                  >
                    <span className={`text-sm ${activeTab === "import" ? "text-black" : "text-[#71717A]"}`}>
                      Start from Scratch
                    </span>
                  </button>
                  <button
                    className={`flex-1 py-1 px-3 text-center rounded-md ${activeTab === "template" ? "bg-white shadow-sm" : ""}`}
                    onClick={() => setActiveTab("template")}
                  >
                    <span className={`text-sm ${activeTab === "template" ? "text-black" : "text-[#71717A]"}`}>
                      Start from Pre-existing
                    </span>
                  </button>
                </div>
              </div>
          )}

            {activeTab === "import" ? renderImportContent() : renderTemplateContent()}
        </>
      )}
    </>
  );

  // If multi-file type, render as drawer
  if (isAgentTypeMultiFile) {
    return (
      <Drawer isOpen={isOpen} placement={"right" as any} onClose={onClose} isDismissable={false}>
        <DrawerContent className="drawer-content max-w-[488px]">
          <DrawerHeader className='border-b-1 flex items-center justify-between'>
            <h2 className="font-semibold text-[18px] leading-[28px] text-[#11181C]">Import Controls</h2>

          </DrawerHeader>
          <DrawerBody className='drawer-body py-4 px-6 flex flex-col h-full'>
            {renderContent()}
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    );
  }

  // Otherwise render as modal
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" className="max-w-[500px]">
      <ModalContent>
        <ModalHeader className="flex flex-col items-center gap-2 p-6 pb-2">
          <h2 className="font-semibold text-[18px] leading-[28px] tracking-[0%] text-center text-[#11181C]">
            Import Controls
          </h2>
          {!isImporting && (
            <p className="text-[14px] font-normal text-[#F31260] text-center">
              Note that all existing controls will be overridden.
            </p>
          )}
        </ModalHeader>

        <ModalBody className="px-6 pb-0 pt-0">
          {renderContent()}
        </ModalBody>

        {!isImporting && !isAgentTypeMultiFile && (
          <ModalFooter className="flex gap-2 pt-4 px-6 pb-6 border-t justify-end">
            <ZButton
              variant="bordered"
              size="sm"
              className="border-1 font-medium"
              onClick={onClose}
              isDisabled={isDownloading || isImporting}
            >
              Cancel
            </ZButton>
            <ZButton
              color="primary"
              size="sm"
              className="font-medium"
              onClick={handleImport}
              isLoading={isImporting}
              isDisabled={!hasValidFiles || isDownloading || isImporting}
            >
              Import
            </ZButton>
          </ModalFooter>
        )}
      </ModalContent>
    </Modal>
  );
};

export default ImportModal;
